import React, { useEffect, useState } from 'react';
import { Layout, Spin } from 'antd';
import {
  Switch,
  Route,
  Redirect,
  useHistory,
  useLocation,
} from 'react-router-dom';
import { useQuery } from 'react-apollo';
import get from 'lodash/get';
import store from 'store2';

import { AppWrapper } from './styles';
import Sidebar, { activeNav } from './components/Sidebar';
import { SESSION } from './pages/apollo_queries';
import { canSeeTreasury } from './treasuryAccess';
import { Properties } from './pages/Properties';
import { PropertyDetails } from './pages/PropertyDetails';
import { AddProperty } from './pages/AddProperty';
import { Investors } from './pages/Investors';
import { InvestorDetails } from './pages/InvestorDetails';
import { InvestmentRules } from './pages/InvestmentRules';
import { Treasury } from './pages/Treasury';

const { Sider, Header, Content } = Layout;

// The admin is four modules: Treasury (2026-09-17: the house's Sila wallet, the home), Properties / notes
// and Investors / users (2026-09-08), plus Investment
// rules (2026-09-10: product minimums + per-investor exceptions, replacing the Retool screens). The tag
// catalog is not managed here (TL, 2026-09-09) — the note form only picks from it via the `tags` query.
// Shell (2026-09-09): antd Sider (brand · menu · operator) + Header (section title) + Content card.
function ClientApp() {
  const history = useHistory();
  const location = useLocation();
  // Reconnected to constlending-api-next: `session` replaces the legacy `initializeAuth`.
  const { data, error, loading } = useQuery(SESSION);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    function authCheck() {
      if (error && !loading) {
        const networkErrorString = get(
          error,
          'networkError.result.errors[0].message',
          ''
        );
        // constlending-api-next answers a missing/expired token with code UNAUTHENTICATED.
        const graphQLErrorCode = get(
          error,
          'graphQLErrors[0].extensions.code',
          ''
        );

        if (
          (networkErrorString &&
            (networkErrorString.includes('jwt expired') ||
              networkErrorString.includes('Login required'))) ||
          graphQLErrorCode === 'UNAUTHENTICATED' ||
          error.message.includes('Login required') ||
          error.message.includes('LendingBearer token')
          // A network blip (backend restarting, dev server recompiling) is NOT a logout: keep the token.
        ) {
          store.remove('constitution-token');
          history.push('/login?error=login_required');
        }
      }
    }

    authCheck();
  }, [error, history, loading]);

  const operator = get(data, 'session.user', null);
  const section = activeNav(location.pathname);
  // PROD-only Treasury gate (TL 2026-10-14): in production only the allowlisted operator sees Treasury;
  // everyone else is kept off the route and both treasury redirects point elsewhere. Open in dev/sandbox.
  // The backend guard is the authority — this only keeps a blocked admin off a screen that would 403.
  const treasuryAllowed = canSeeTreasury(get(operator, 'email'));
  const treasuryHome = treasuryAllowed ? '/treasury' : '/properties-and-investments';

  return (
    <AppWrapper>
      <Layout>
        <Sider
          collapsible
          collapsed={collapsed}
          onCollapse={setCollapsed}
          width={240}
          breakpoint="lg"
        >
          <Sidebar collapsed={collapsed} operator={operator} />
        </Sider>
        <Layout>
          <Header
            style={{
              background: '#fff',
              padding: '0 24px',
              borderBottom: '1px solid #f0f0f0',
              fontSize: 18,
              fontWeight: 600,
              lineHeight: '64px',
            }}
          >
            {section.label}
          </Header>
          <Content style={{ margin: 24 }}>
            <div
              style={{
                background: '#fff',
                padding: 24,
                minHeight: 'calc(100vh - 64px - 48px - 48px)',
              }}
            >
              {loading ? (
                // Wait for the session before routing: the Treasury gate needs the operator's email, and
                // deciding it with a null operator would bounce an allowed user off the home on first load.
                <div style={{ textAlign: 'center', padding: 48 }}>
                  <Spin size="large" />
                </div>
              ) : (
                <Switch>
                  <Redirect from="/" to={treasuryHome} exact />
                  {treasuryAllowed && (
                    <Route path="/treasury" component={Treasury} exact />
                  )}
                  <Route
                    path="/properties-and-investments"
                    component={Properties}
                    exact
                  />
                  <Route
                    path="/properties-and-investments/:id"
                    component={PropertyDetails}
                    exact
                  />
                  <Route path="/add-property" component={AddProperty} exact />
                  <Route path="/users" component={Investors} exact />
                  <Route path="/users/:id" component={InvestorDetails} exact />
                  <Route
                    path="/investment-rules"
                    component={InvestmentRules}
                    exact
                  />
                  {/* Unknown paths (e.g. the retired /tags) and, for a blocked user, /treasury land on
                      the home (treasuryHome: Treasury when allowed, Properties otherwise). */}
                  <Redirect to={treasuryHome} />
                </Switch>
              )}
            </div>
          </Content>
        </Layout>
      </Layout>
    </AppWrapper>
  );
}

export default ClientApp;
